import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Loader2, AlertTriangle, XCircle, Upload, Eye, EyeOff } from 'lucide-react';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('es-DO', { year: 'numeric', month: 'short', day: '2-digit' }) : '-');
const cn = (dn, key = 'CN') => (dn || '').split(',').map(p => p.trim()).find(p => p.startsWith(`${key}=`))?.slice(key.length + 1) || dn || '-';

const estadoVencimiento = (c) => {
  if (c.diasRestantes <= 0) return { label: 'Vencido', cls: 'bg-red-100 text-red-700 border-red-200', row: 'bg-red-50/60' };
  if (c.proximoAExpirar || c.diasRestantes <= 30) return { label: 'Por vencer', cls: 'bg-amber-100 text-amber-800 border-amber-200', row: 'bg-amber-50/60' };
  return { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', row: '' };
};

const AlertaVencimiento = ({ certs }) => {
  const activo = certs.find(c => c.esActivo);
  if (!activo) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cert-alert-no-active">
        <XCircle size={18} /> No hay un certificado activo. No se podrán firmar comprobantes electrónicos.
      </div>
    );
  }
  const est = estadoVencimiento(activo);
  if (est.label === 'Vigente') return null;
  return (
    <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${est.cls}`} data-testid="cert-alert-expiry">
      <AlertTriangle size={18} />
      {est.label === 'Vencido'
        ? 'El certificado activo está vencido. Debe subir uno nuevo en el proveedor.'
        : `El certificado activo vence en ${activo.diasRestantes} días (${fmtDate(activo.fechaExpiracion)}).`}
    </div>
  );
};

const UploadCertDialog = ({ onUploaded }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setFile(null); setPassword(''); setError(''); setShowPwd(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !password) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('certificado', file);
      fd.append('password', password);
      const resp = await axios.post(`${API}/ecf/certificados/subir`, fd);
      toast.success(resp.data.message || 'Certificado subido correctamente');
      setOpen(false);
      reset();
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error subiendo certificado');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-upload-cert-btn">
          <Upload className="mr-2" size={16} /> Subir Certificado
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="upload-cert-dialog">
        <DialogHeader>
          <DialogTitle>Subir Certificado Digital</DialogTitle>
          <DialogDescription>El archivo y su contraseña se envían directo al proveedor; no se guarda copia en este sistema.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cert-file">Archivo (.p12 / .pfx) *</Label>
            <Input id="cert-file" type="file" accept=".p12,.pfx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cert-file-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cert-password">Contraseña del certificado *</Label>
            <div className="relative">
              <Input id="cert-password" type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="cert-password-input" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1} data-testid="toggle-cert-password-btn">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="upload-cert-error">
              <XCircle size={18} /> {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-upload-cert-btn">Cancelar</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={uploading || !file || !password} data-testid="submit-upload-cert-btn">
              {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload className="mr-2" size={16} />}
              Subir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Certificados = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCerts = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${API}/ecf/certificados`);
      setCerts(resp.data.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error consultando certificados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCerts(); }, []);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="certificados-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="text-emerald-600" size={28} /> Certificados
          </h1>
          <p className="text-slate-500 text-sm mt-1">Certificados digitales (.p12) registrados en el proveedor de Facturación Electrónica.</p>
        </div>
        <div className="flex gap-2">
          <UploadCertDialog onUploaded={fetchCerts} />
          <Button variant="outline" onClick={fetchCerts} disabled={loading} data-testid="refresh-certificados-btn">
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw className="mr-2" size={16} />}
            Actualizar
          </Button>
        </div>
      </div>

      {!loading && !error && <AlertaVencimiento certs={certs} />}

      <Card>
        <CardHeader>
          <CardTitle>Listado</CardTitle>
          <CardDescription data-testid="certificados-total">{loading ? 'Cargando…' : `${certs.length} certificado(s)`}</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="certificados-error">
              <XCircle size={18} /> {error}
            </div>
          ) : loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-emerald-500" size={24} /></div>
          ) : certs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="certificados-empty">No hay certificados registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="certificados-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Titular</TableHead>
                    <TableHead>Emisor</TableHead>
                    <TableHead>Emisión</TableHead>
                    <TableHead>Expiración</TableHead>
                    <TableHead>Subido</TableHead>
                    <TableHead className="text-right">Días</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certs.map(c => {
                    const est = estadoVencimiento(c);
                    return (
                      <TableRow key={c.id} className={est.row} data-testid={`cert-row-${c.id}`}>
                        <TableCell className="font-mono text-xs max-w-[220px] truncate" title={c.nombreArchivo}>{c.nombreArchivo}</TableCell>
                        <TableCell className="text-sm" title={c.subject}>{cn(c.subject)}</TableCell>
                        <TableCell className="text-sm" title={c.emisor}>{cn(c.emisor)}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{fmtDate(c.fechaEmision)}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{fmtDate(c.fechaExpiracion)}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{fmtDate(c.fechaSubida)}</TableCell>
                        <TableCell className="text-right font-semibold" data-testid={`cert-dias-${c.id}`}>{c.diasRestantes}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {c.esActivo
                              ? <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={`cert-activo-${c.id}`}>Activo</Badge>
                              : <Badge variant="outline" className="text-slate-500">Inactivo</Badge>}
                            <Badge variant="outline" className={est.cls} data-testid={`cert-vencimiento-${c.id}`}>{est.label}</Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Certificados;
